// THE FILE READ — resolving an attachment id to something a person can look at.
//
// Until this module existed the SPA could only ever WRITE files. Every
// `/v1/files` call in apps/web was in the floor PWA's capture path (register →
// PUT → store); nothing anywhere read one back. So an operator could photograph
// a damaged drum, the bytes were verified and frozen server-side under
// `waste_evidence`, the office drawer said "Photos 1" — and no screen in the
// product could show it to anybody. Evidence a plant takes pictures for has to
// be viewable or the capture is theatre (surface review, 2026-08-13).
//
// `GET /v1/files/{id}` is the tenant-fenced resolver: it mints a SHORT-LIVED
// presigned GET from the row's own object key (no endpoint anywhere accepts a
// key from a caller), and a file belonging to another tenant answers 404 rather
// than 403 — the no-id-oracle rule. So the client never needs, and never gets,
// an object key.
//
// WHY EACH ID IS ITS OWN QUERY. There is no batch resolve on the wire, and one
// id failing must not blank the others: a rejected photo among three good ones
// shows two thumbnails and one honest "this one cannot be shown", not an empty
// strip. `useQueries` gives per-id state for free.
//
// EXPIRY. `download_expires_at` is minutes, not hours. `staleTime` is
// deliberately well inside it so a drawer left open across a coffee break
// re-mints rather than rendering a dead URL, and `gcTime` matches: a presigned
// URL is not worth caching past its own life.

import { useQueries } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "./api";
import { unwrap } from "./request";

type FileRecord = components["schemas"]["FileRecord"];

/** A minted presigned URL is treated as good for this long, then re-fetched. */
const PRESIGN_STALE_MS = 60_000;

export const fileKeys = {
  /** One key per file id — `["file", id]`. */
  download: (id: string) => ["file", id] as const,
};

/** What one attachment id resolved to, for rendering. */
export interface ResolvedFile {
  readonly id: string;
  /** True while the resolve is in flight — a state that fixes itself. */
  readonly isPending: boolean;
  /** True when the server refused or could not find it (404/409/403). */
  readonly isFailed: boolean;
  /** The presigned GET, once minted. */
  readonly url: string | undefined;
  readonly record: FileRecord | undefined;
}

/** Resolve every attachment id to a short-lived download URL. */
export function useFileDownloads(ids: readonly string[]): ResolvedFile[] {
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: fileKeys.download(id),
      staleTime: PRESIGN_STALE_MS,
      gcTime: PRESIGN_STALE_MS,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const res = await client.GET("/v1/files/{id}", { signal, params: { path: { id } } });
        return unwrap(res, "Could not open the file");
      },
    })),
  });
  return results.map((result, index) => ({
    id: ids[index] ?? "",
    isPending: result.isPending,
    isFailed: result.isError,
    url: result.data?.download_url,
    record: result.data?.file,
  }));
}

/** True when the resolved file is something an `<img>` can render. */
export function isViewableImage(record: FileRecord | undefined): boolean {
  return record?.status === "stored" && record.content_type.startsWith("image/");
}
